const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://sekaie-work-app.valleyintest.workers.dev';

const TOKEN_KEY = 'auth_token';

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function removeToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: 'APIリクエストに失敗しました' };
    }
    throw new ApiError(
      response.status,
      errorData.code || 'UNKNOWN_ERROR',
      errorData.error || errorData.message || 'APIリクエストに失敗しました',
      errorData.details
    );
  }

  return response.json();
}

export async function uploadRequest<T>(
  endpoint: string,
  formData: FormData
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: 'ファイルアップロードに失敗しました' };
    }
    throw new ApiError(
      response.status,
      errorData.code || 'UPLOAD_ERROR',
      errorData.error || errorData.message || 'ファイルアップロードに失敗しました',
      errorData.details
    );
  }

  return response.json();
}

export interface UploadOptions {
  entityType?: string;
  entityId?: string;
  folder?: string;
}

export const api = {
  get: <T>(endpoint: string) => apiRequest<T>(endpoint),
  post: <T>(endpoint: string, data: unknown) =>
    apiRequest<T>(endpoint, { method: 'POST', body: JSON.stringify(data) }),
  put: <T>(endpoint: string, data: unknown) =>
    apiRequest<T>(endpoint, { method: 'PUT', body: JSON.stringify(data) }),
  delete: <T>(endpoint: string) =>
    apiRequest<T>(endpoint, { method: 'DELETE' }),
  upload: <T>(endpoint: string, formData: FormData) =>
    uploadRequest<T>(endpoint, formData),
  
  // ファイルアップロード用の便利メソッド
  uploadFile: async (file: File, options?: UploadOptions): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    
    if (options?.entityType) {
      formData.append('entityType', options.entityType);
    }
    if (options?.entityId) {
      formData.append('entityId', options.entityId);
    }
    if (options?.folder) {
      formData.append('folder', options.folder);
    }

    const response = await uploadRequest<{ url: string; key: string }>('/api/upload', formData);
    return response.url;
  },

  // 複数ファイルのアップロード
  uploadFiles: async (files: File[], options?: UploadOptions): Promise<string[]> => {
    const uploadPromises = files.map(file => api.uploadFile(file, options));
    return Promise.all(uploadPromises);
  },
};
